import { apiClient } from '@/lib/api';
import type {
  ImportOrder,
  ImportOrdersResponse,
  ImportOrdersListParams,
  ImportOrderItem,
  CostBreakdown,
  ImportDocument,
  TrackingEvent,
  ImportAuditLog,
  ImportDashboardStats,
  ImportsByStatusChart,
  MonthlyImportTrend,
  TopSupplier,
  CostVarianceAlert,
  LandedCostInput,
  LandedCostResult,
  ImportSupplier,
  CreateImportOrderInput,
  UpdateImportOrderInput,
  CreateImportItemInput,
  CreateTrackingEventInput,
  CreateImportSupplierInput,
  ContainerType,
  Container,
  ContainerItem,
  CreateContainerInput,
  UpdateContainerInput,
  CreateContainerItemInput,
  PendingStowItem,
  UpdateContainerItemInput,
  CreateContainerTypeInput,
  BulkPositionUpdate,
  AutoCalculateResult,
  AvailableContainer,
  PortSummary,
  ImportOrderLeg,
  TransportMode,
} from '../types/import-tracking.types';

const BASE = '/api/import-tracking';

export const importTrackingService = {
  // ── Orders ─────────────────────────────────────────────────────

  getOrders: async (params?: ImportOrdersListParams): Promise<ImportOrdersResponse> => {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.status) query.set('status', params.status);
    if (params?.freightProviderId) query.set('freightProviderId', params.freightProviderId);
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params?.dateTo) query.set('dateTo', params.dateTo);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortOrder) query.set('sortOrder', params.sortOrder);
    if (params?.stage) query.set('stage', params.stage);
    if (params?.isQuote !== undefined) query.set('isQuote', String(params.isQuote));
    const qs = query.toString();
    return apiClient.get(`${BASE}/orders${qs ? `?${qs}` : ''}`);
  },

  getOrder: async (id: string): Promise<ImportOrder> => {
    return apiClient.get(`${BASE}/orders/${id}`);
  },

  createOrder: async (data: CreateImportOrderInput): Promise<ImportOrder> => {
    return apiClient.post(`${BASE}/orders`, data);
  },

  updateOrder: async (id: string, data: UpdateImportOrderInput): Promise<ImportOrder> => {
    return apiClient.put(`${BASE}/orders/${id}`, data);
  },

  deleteOrder: async (id: string): Promise<void> => {
    return apiClient.delete(`${BASE}/orders/${id}`);
  },

  // ── Share token (portal cliente) ────────────────────────────────

  shareOrder: async (
    id: string,
    expiresInDays?: number
  ): Promise<{ token: string; expiresAt: string | null }> => {
    return apiClient.post(`${BASE}/orders/${id}/share`, { expiresInDays });
  },

  revokeOrderShare: async (id: string): Promise<void> => {
    return apiClient.delete(`${BASE}/orders/${id}/share`);
  },

  // ── Order Items ────────────────────────────────────────────────

  getOrderItems: async (orderId: string): Promise<ImportOrderItem[]> => {
    return apiClient.get(`${BASE}/orders/${orderId}/items`);
  },

  addOrderItem: async (orderId: string, data: CreateImportItemInput): Promise<ImportOrderItem> => {
    return apiClient.post(`${BASE}/orders/${orderId}/items`, data);
  },

  /**
   * Carga muchos productos de una vez, en una transacción.
   *
   * Antes se hacía una petición por línea: con las 520 de una proforma real eran
   * ~60 s de espera, y si se cortaba a mitad la orden quedaba a medias sin forma de
   * saber qué entró. Acá es todo o nada.
   */
  addOrderItemsBulk: async (
    orderId: string,
    items: CreateImportItemInput[]
  ): Promise<{ imported: number; requested: number }> => {
    return apiClient.post(
      `${BASE}/orders/${orderId}/items/bulk`,
      { items },
      {
        // 520 filas en una transacción: el timeout por defecto es muy corto.
        timeout: 120_000,
      }
    );
  },

  updateOrderItem: async (
    orderId: string,
    itemId: string,
    data: Partial<CreateImportItemInput>
  ): Promise<ImportOrderItem> => {
    return apiClient.put(`${BASE}/orders/${orderId}/items/${itemId}`, data);
  },

  deleteOrderItem: async (orderId: string, itemId: string): Promise<void> => {
    return apiClient.delete(`${BASE}/orders/${orderId}/items/${itemId}`);
  },

  // ── Order Parties (Magaya tab "Entidades") ─────────────────────

  listParties: async (
    orderId: string
  ): Promise<import('../types/import-tracking.types').ImportOrderParty[]> => {
    return apiClient.get(`${BASE}/orders/${orderId}/parties`);
  },

  createParty: async (
    orderId: string,
    data: import('../types/import-tracking.types').CreateImportPartyInput
  ): Promise<import('../types/import-tracking.types').ImportOrderParty> => {
    return apiClient.post(`${BASE}/orders/${orderId}/parties`, data);
  },

  updateParty: async (
    orderId: string,
    partyId: string,
    data: import('../types/import-tracking.types').UpdateImportPartyInput
  ): Promise<import('../types/import-tracking.types').ImportOrderParty> => {
    return apiClient.put(`${BASE}/orders/${orderId}/parties/${partyId}`, data);
  },

  deleteParty: async (orderId: string, partyId: string): Promise<void> => {
    return apiClient.delete(`${BASE}/orders/${orderId}/parties/${partyId}`);
  },

  // ── Cost Breakdown ─────────────────────────────────────────────

  getCostBreakdown: async (orderId: string): Promise<CostBreakdown> => {
    return apiClient.get(`${BASE}/orders/${orderId}/costs`);
  },

  updateCosts: async (orderId: string, data: Partial<CostBreakdown>): Promise<CostBreakdown> => {
    return apiClient.put(`${BASE}/orders/${orderId}/costs`, data);
  },

  // ── Documents ──────────────────────────────────────────────────

  getDocuments: async (orderId: string): Promise<ImportDocument[]> => {
    return apiClient.get(`${BASE}/orders/${orderId}/documents`);
  },

  uploadDocument: async (
    orderId: string,
    file: File,
    type: string,
    name: string
  ): Promise<ImportDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    formData.append('name', name);
    return apiClient.post(`${BASE}/orders/${orderId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * Descarga el archivo adjunto.
   *
   * Pasa por `apiClient` y no por un `<a href>` porque la ruta exige el token:
   * el archivo se sirve con verificación de tenant, no como estático. Un enlace
   * directo daría 401 — y antes apuntaba a `fileUrl`, que es la ruta interna en
   * disco y nunca fue una URL que el navegador pudiera abrir.
   */
  downloadDocument: async (orderId: string, docId: string, fileName: string): Promise<void> => {
    const blob = await apiClient.get<Blob>(
      `${BASE}/orders/${orderId}/documents/${docId}/download`,
      { responseType: 'blob' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  },

  deleteDocument: async (orderId: string, docId: string): Promise<void> => {
    return apiClient.delete(`${BASE}/orders/${orderId}/documents/${docId}`);
  },

  // ── Tracking Events ────────────────────────────────────────────

  getTrackingEvents: async (orderId: string): Promise<TrackingEvent[]> => {
    return apiClient.get(`${BASE}/orders/${orderId}/tracking`);
  },

  addTrackingEvent: async (
    orderId: string,
    data: CreateTrackingEventInput
  ): Promise<TrackingEvent> => {
    return apiClient.post(`${BASE}/orders/${orderId}/tracking`, data);
  },

  // ── Audit Logs ─────────────────────────────────────────────────

  getAuditLogs: async (orderId: string): Promise<ImportAuditLog[]> => {
    return apiClient.get(`${BASE}/orders/${orderId}/history`);
  },

  // ── Dashboard / KPIs ───────────────────────────────────────────

  getDashboardStats: async (): Promise<ImportDashboardStats> => {
    return apiClient.get(`${BASE}/dashboard/stats`);
  },

  getImportsByStatus: async (): Promise<ImportsByStatusChart[]> => {
    return apiClient.get(`${BASE}/dashboard/by-status`);
  },

  getMonthlyTrends: async (months?: number): Promise<MonthlyImportTrend[]> => {
    const qs = months ? `?months=${months}` : '';
    return apiClient.get(`${BASE}/dashboard/monthly-trends${qs}`);
  },

  getTopSuppliers: async (limit?: number): Promise<TopSupplier[]> => {
    const qs = limit ? `?limit=${limit}` : '';
    return apiClient.get(`${BASE}/dashboard/top-suppliers${qs}`);
  },

  getCostVarianceAlerts: async (): Promise<CostVarianceAlert[]> => {
    return apiClient.get(`${BASE}/dashboard/cost-variance`);
  },

  // ── Landed Cost Calculator ─────────────────────────────────────

  calculateLandedCost: async (data: LandedCostInput): Promise<LandedCostResult> => {
    return apiClient.post(`${BASE}/calculate-landed-cost`, data);
  },

  applyLandedCostToModel: async (
    modelId: string,
    costs: Record<string, number>
  ): Promise<{ updated: number }> => {
    return apiClient.post(`${BASE}/apply-to-model`, { modelId, costs });
  },

  // ── Suppliers ──────────────────────────────────────────────────

  getSuppliers: async (): Promise<ImportSupplier[]> => {
    return apiClient.get(`${BASE}/suppliers`);
  },

  createSupplier: async (data: CreateImportSupplierInput): Promise<ImportSupplier> => {
    return apiClient.post(`${BASE}/suppliers`, data);
  },

  updateSupplier: async (
    id: string,
    data: Partial<CreateImportSupplierInput>
  ): Promise<ImportSupplier> => {
    return apiClient.put(`${BASE}/suppliers/${id}`, data);
  },

  deleteSupplier: async (id: string): Promise<void> => {
    return apiClient.delete(`${BASE}/suppliers/${id}`);
  },

  // ── Container Types ──────────────────────────────────────────────

  getContainerTypes: async (): Promise<ContainerType[]> => {
    return apiClient.get(`${BASE}/container-types`);
  },

  createContainerType: async (data: CreateContainerTypeInput): Promise<ContainerType> => {
    return apiClient.post(`${BASE}/container-types`, data);
  },

  updateContainerType: async (
    id: string,
    data: Partial<CreateContainerTypeInput>
  ): Promise<ContainerType> => {
    return apiClient.put(`${BASE}/container-types/${id}`, data);
  },

  deleteContainerType: async (id: string): Promise<void> => {
    return apiClient.delete(`${BASE}/container-types/${id}`);
  },

  // ── Containers ───────────────────────────────────────────────────

  getOrderContainers: async (orderId: string): Promise<Container[]> => {
    return apiClient.get(`${BASE}/orders/${orderId}/containers`);
  },

  getContainer: async (id: string): Promise<Container> => {
    return apiClient.get(`${BASE}/containers/${id}`);
  },

  createContainer: async (orderId: string, data: CreateContainerInput): Promise<Container> => {
    return apiClient.post(`${BASE}/orders/${orderId}/containers`, data);
  },

  updateContainer: async (id: string, data: UpdateContainerInput): Promise<Container> => {
    return apiClient.put(`${BASE}/containers/${id}`, data);
  },

  deleteContainer: async (id: string): Promise<void> => {
    return apiClient.delete(`${BASE}/containers/${id}`);
  },

  // ── Container Items ──────────────────────────────────────────────

  getContainerItems: async (containerId: string): Promise<ContainerItem[]> => {
    return apiClient.get(`${BASE}/containers/${containerId}/items`);
  },

  addContainerItem: async (
    containerId: string,
    data: CreateContainerItemInput
  ): Promise<ContainerItem> => {
    return apiClient.post(`${BASE}/containers/${containerId}/items`, data);
  },

  /**
   * Los productos de la orden que todavía no están en ningún contenedor.
   *
   * Es lo que evita cargar la mercadería dos veces: entra una vez con la
   * proforma y de ahí se estiba.
   */
  getPendingStowItems: async (containerId: string): Promise<{ items: PendingStowItem[] }> => {
    return apiClient.get(`${BASE}/containers/${containerId}/pending-items`);
  },

  /** Estiba en el contenedor las unidades elegidas de cada producto. */
  stowFromOrder: async (
    containerId: string,
    items: Array<{ orderItemId: string; quantity: number }>
  ): Promise<{ stowed: number; skipped: Array<{ description: string; reason: string }> }> => {
    return apiClient.post(`${BASE}/containers/${containerId}/items/from-order`, { items });
  },

  updateContainerItem: async (
    containerId: string,
    itemId: string,
    data: UpdateContainerItemInput
  ): Promise<ContainerItem> => {
    return apiClient.put(`${BASE}/containers/${containerId}/items/${itemId}`, data);
  },

  deleteContainerItem: async (containerId: string, itemId: string): Promise<void> => {
    return apiClient.delete(`${BASE}/containers/${containerId}/items/${itemId}`);
  },

  // ── Container Positions ──────────────────────────────────────────

  saveContainerPositions: async (containerId: string, data: BulkPositionUpdate): Promise<void> => {
    return apiClient.put(`${BASE}/containers/${containerId}/items/positions`, data);
  },

  // ── Auto Calculate ────────────────────────────────────────────

  autoCalculate: async (
    orderId: string,
    params?: {
      insuranceRate?: number;
      defaultDutyRate?: number;
      taxRate?: number;
      internacionRate?: number;
    }
  ): Promise<AutoCalculateResult> => {
    return apiClient.post(`${BASE}/orders/${orderId}/auto-calculate`, params || {});
  },

  // ── Publish Prices to Price Chain ────────────────────────────
  publishPrices: async (
    orderId: string,
    quoteTypeId: string
  ): Promise<{ publishedCount: number; message: string }> => {
    return apiClient.post(`${BASE}/orders/${orderId}/publish-prices`, { quoteTypeId });
  },

  // ── Convert Quote to Order ──────────────────────────────────
  convertToOrder: async (orderId: string): Promise<ImportOrder> => {
    return apiClient.post(`${BASE}/orders/${orderId}/convert-to-order`);
  },

  // ── Freight Breakdown ─────────────────────────────────────────
  getFreightBreakdown: async (
    orderId: string
  ): Promise<{ totalFreight: number; incoterm: string; legBreakdown: any[] }> => {
    return apiClient.get(`${BASE}/orders/${orderId}/freight-breakdown`);
  },

  // ── Available Containers (Availability Map) ──────────────────

  getAvailableContainers: async (): Promise<AvailableContainer[]> => {
    return apiClient.get(`${BASE}/containers/available`);
  },

  getAvailableContainersByPort: async (): Promise<PortSummary[]> => {
    return apiClient.get(`${BASE}/containers/available/by-port`);
  },

  // ── Order Legs ────────────────────────────────────────────────

  getOrderLegs: async (orderId: string): Promise<ImportOrderLeg[]> => {
    return apiClient.get(`${BASE}/orders/${orderId}/legs`);
  },

  createLeg: async (
    orderId: string,
    dto: {
      origin: string;
      destination: string;
      transportMode: TransportMode;
      legType: 'INLAND_ORIGIN' | 'INTERNATIONAL' | 'INLAND_DESTINATION' | 'HANDLING';
      expectedStart?: string | null;
      expectedEnd?: string | null;
      carrier?: string | null;
      trackingRef?: string | null;
      notes?: string | null;
      plannedAmount?: number | null;
      currency?: string;
      // Pricing
      cargoWeightKg?: number | null;
      cargoVolumeCbm?: number | null;
      legCatalogId?: string | null;
      providerId?: string | null;
      pricingType?: 'WEIGHT' | 'VOLUME' | 'FLAT' | null;
      unitPrice?: number | null;
      minimumCharge?: number | null;
    }
  ): Promise<ImportOrderLeg> => {
    return apiClient.post(`${BASE}/orders/${orderId}/legs`, dto);
  },

  updateLeg: async (legId: string, dto: Partial<ImportOrderLeg>): Promise<ImportOrderLeg> => {
    return apiClient.patch(`${BASE}/legs/${legId}`, dto);
  },

  deleteLeg: async (legId: string): Promise<void> => {
    return apiClient.delete(`${BASE}/legs/${legId}`);
  },

  advanceLeg: async (legId: string): Promise<ImportOrderLeg> => {
    return apiClient.post(`${BASE}/legs/${legId}/advance`);
  },

  // ── Servicios de tramo (Phase 3) ─────────────────────────────────
  getLegServices: async (legId: string) => {
    return apiClient.get<import('../types/import-tracking.types').ImportOrderLegService[]>(
      `${BASE}/legs/${legId}/services`
    );
  },

  createLegService: async (
    legId: string,
    dto: {
      serviceId?: string | null;
      providerId?: string | null;
      name: string;
      pricingType: 'WEIGHT' | 'VOLUME' | 'FLAT';
      unitPrice: number;
      minimumCharge?: number | null;
      currency?: string;
      notes?: string | null;
    }
  ) => {
    return apiClient.post<import('../types/import-tracking.types').ImportOrderLegService[]>(
      `${BASE}/legs/${legId}/services`,
      dto
    );
  },

  updateLegService: async (
    legId: string,
    serviceLineId: string,
    dto: Partial<{
      name: string;
      pricingType: 'WEIGHT' | 'VOLUME' | 'FLAT';
      unitPrice: number;
      minimumCharge: number | null;
      currency: string;
      notes: string | null;
      providerId: string | null;
    }>
  ) => {
    return apiClient.patch<import('../types/import-tracking.types').ImportOrderLegService>(
      `${BASE}/legs/${legId}/services/${serviceLineId}`,
      dto
    );
  },

  deleteLegService: async (legId: string, serviceLineId: string): Promise<void> => {
    return apiClient.delete(`${BASE}/legs/${legId}/services/${serviceLineId}`);
  },
};
